/**
 * Drives an athlete tab bar that swaps a background video + quote card,
 * with autoplay that cycles through athletes and pauses on hover/interaction.
 */
class AthleteVideoTabs extends HTMLElement {
  /** @type {number} */
  #current = 0;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.pills = Array.from(this.querySelectorAll('.athlete-tabs__pill'));
    if (this.pills.length < 1) return;

    this.pills.forEach((pill, index) => {
      pill.addEventListener('click', () => this.select(index, true));
      pill.addEventListener('keydown', (event) => this.#handleKeyDown(event, index));
    });

    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.play);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    const speedAttr = parseFloat(this.dataset.autoplaySpeed || '5');
    this.speed = (Number.isNaN(speedAttr) ? 5 : speedAttr) * 1000;

    this.select(0, false);
    this.play();
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * @param {number} index
   * @param {boolean} userInitiated
   */
  select(index, userInitiated) {
    if (index === this.#current && userInitiated === false) return;

    const previousIndex = this.#current;
    this.#current = index;

    this.pills.forEach((pill, i) => {
      pill.classList.toggle('is-active', i === index);
      pill.setAttribute('aria-selected', String(i === index));
    });

    this.querySelectorAll(`[data-athlete-index="${previousIndex}"]`).forEach((panel) => {
      panel.classList.remove('is-active');
      const video = panel.querySelector('video');
      if (video instanceof HTMLVideoElement) video.pause();
    });

    this.querySelectorAll(`[data-athlete-index="${index}"]`).forEach((panel) => {
      panel.classList.add('is-active');
      const video = panel.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    });

    if (userInitiated) this.#restart();
  }

  next = () => {
    this.select((this.#current + 1) % this.pills.length, false);
  };

  play = () => {
    if (this.pills.length < 2) return;
    this.pause();
    this.#timer = window.setInterval(this.next, this.speed);
  };

  pause = () => {
    window.clearInterval(this.#timer);
  };

  #restart() {
    this.play();
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else {
      this.play();
    }
  };

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #handleKeyDown(event, index) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (index + direction + this.pills.length) % this.pills.length;

    this.select(nextIndex, true);
    this.pills[nextIndex]?.focus();
  }
}

if (!customElements.get('athlete-video-tabs')) {
  customElements.define('athlete-video-tabs', AthleteVideoTabs);
}
